import {OverviewTableState} from '../states/overview-table.state';
import {createReducer, on} from '@ngrx/store';
import {OverviewTableActions} from '../actions/overview-table.actions';
import {
  BelegbucheinAusgangDTO,
  BelegZahlungsartDTO,
  FilterBelegDTO,
  PageableBelegDTOSortingEnum,
} from '../../openapi/beleg-openapi';
import {BelegEntitiesActions} from '../actions/beleg-entities.actions';
import {FilterChipInputIdentifier} from '../../interfaces/filter-chip-input-identifier.interface';
import * as moment from 'moment/moment';
import {v4 as uuid} from 'uuid';
import {BelegeTableColumns} from '../../modules/overview/belege-list/belege-table/belege-table.columns';
import {formatNumber} from '@angular/common';

function formatBetrag(value: number): string {
  return formatNumber(value, 'de-DE', '1.2-2');
}

function hasActiveFilters(filterDto: FilterBelegDTO): boolean {
  return !!(
    (filterDto.textfilter && filterDto.textfilter.length > 0) ||
    filterDto.offen ||
    filterDto.bearbeitet ||
    filterDto.festgeschrieben ||
    filterDto.storniert ||
    filterDto.erstelltVon ||
    filterDto.erstelltBis ||
    filterDto.vorgangsartIds?.length ||
    filterDto.eingangsbeleg ||
    filterDto.ausgangsbeleg ||
    filterDto.umsatzZugeordnet ||
    filterDto.keinUmsatzZugeordnet ||
    filterDto.dublettenOnly ||
    filterDto.datumVon ||
    filterDto.datumBis ||
    (filterDto.betragVon !== undefined && filterDto.betragVon !== null) ||
    (filterDto.betragBis !== undefined && filterDto.betragBis !== null) ||
    (filterDto.zahlungsarten as any)?.length
  );
}

export const initialOverviewTableState: OverviewTableState = {
  filterDto: {},
  filterVisible: false,
  isLoading: true,
  pageableDto: {
    limit: 50,
    offset: 0,
    sorting: [PageableBelegDTOSortingEnum.Erstelltdesc],
  },
  selectedIds: [],
  displayedIds: [],
  chipInputIdentifiers: [],
  tableSettings: {
    _key: 'belege-online-app-overview-table-settings-2026-03-16_1',
    _columns: [
      {
        id: BelegeTableColumns.Checkbox,
        name: '',
        isActive: true,
        sortable: false,
        disableRowClick: true,
      },
      {
        id: BelegeTableColumns.Status,
        name: 'Status',
        isActive: true,
        sortable: true,
        sortIds: {
          asc: PageableBelegDTOSortingEnum.Statusasc,
          desc: PageableBelegDTOSortingEnum.Statusdesc,
        },
      },
      {
        id: BelegeTableColumns.HochgeladenAm,
        name: 'Hochgeladen am',
        isActive: true,
        sortable: true,
        sortIds: {
          asc: PageableBelegDTOSortingEnum.Erstelltasc,
          desc: PageableBelegDTOSortingEnum.Erstelltdesc,
        },
      },
      {
        id: BelegeTableColumns.Belegtyp,
        name: 'Belegtyp',
        isActive: true,
        sortable: true,
        sortIds: {
          asc: PageableBelegDTOSortingEnum.Vorgangsartasc,
          desc: PageableBelegDTOSortingEnum.Vorgangsartdesc,
        },
      },
      {
        id: BelegeTableColumns.EinAusgang,
        name: 'Ein-/Ausgang',
        isActive: false,
        sortable: true,
        sortIds: {
          asc: PageableBelegDTOSortingEnum.Belegbuchasc,
          desc: PageableBelegDTOSortingEnum.Belegbuchdesc,
        },
      },
      {
        id: BelegeTableColumns.Kommentar,
        name: 'Kommentar',
        isActive: true,
        sortable: true,
        width: 'allocate',
        overflow: 'ellipsis',
        sortIds: {
          asc: PageableBelegDTOSortingEnum.Kommentarasc,
          desc: PageableBelegDTOSortingEnum.Kommentardesc,
        },
      },
      {
        id: BelegeTableColumns.KundeLieferant,
        name: 'Kunde/Lieferant',
        isActive: true,
        sortable: true,
        width: 'allocate',
        overflow: 'ellipsis',
        sortIds: {
          asc: PageableBelegDTOSortingEnum.Partnerbezeichnungasc,
          desc: PageableBelegDTOSortingEnum.Partnerbezeichnungdesc,
        },
      },
      {
        id: BelegeTableColumns.Belegdatum,
        name: 'Belegdatum',
        isActive: true,
        sortable: true,
        sortIds: {
          asc: PageableBelegDTOSortingEnum.Datumasc,
          desc: PageableBelegDTOSortingEnum.Datumdesc,
        },
      },
      {
        id: BelegeTableColumns.Betrag,
        name: 'Betrag',
        isActive: true,
        sortable: true,
        alignCell: 'right',
        alignHeader: 'right',
        sortIds: {
          asc: PageableBelegDTOSortingEnum.Betragasc,
          desc: PageableBelegDTOSortingEnum.Betragdesc,
        },
      },
      {
        id: BelegeTableColumns.Seitenzahl,
        name: 'Seitenzahl',
        isActive: false,
        sortable: false,
        alignCell: 'right',
        alignHeader: 'right',
      },
      {
        id: BelegeTableColumns.KontumsatzZugeordnet,
        name: 'Umsatz zugeordnet',
        isActive: false,
        sortable: true,
        sortIds: {
          asc: PageableBelegDTOSortingEnum.Umsatzzugeordnetasc,
          desc: PageableBelegDTOSortingEnum.Umsatzzugeordnetdesc,
        },
      },
      {
        id: BelegeTableColumns.Zahlungsart,
        name: 'Zahlungsart',
        isActive: false,
        sortable: true,
        sortIds: {
          asc: PageableBelegDTOSortingEnum.Zahlungsartasc,
          desc: PageableBelegDTOSortingEnum.Zahlungsartdesc,
        },
      },
      {
        id: BelegeTableColumns.Actions,
        name: '',
        isActive: true,
        sortable: false,
        disableRowClick: true,
      },
    ],
    _sortDirs: ['desc'],
    _sortParams: [BelegeTableColumns.HochgeladenAm],
  },
  tableSettingsAreInitialized: false,
};

/*
 * INFO:
 * Alle Actions, die den `filterDto` verändern. Jede neue filterändernde Action (jeder Handler,
 * der `filterDto` anfasst) MUSS hier eingetragen werden — sonst kehrt der Offset-Reset-Bug für
 * den neuen Filter zurück (siehe Handler am Ende des Reducers).
 */
export const filterChangingActions = [
  OverviewTableActions.updateSearchFilter,
  OverviewTableActions.updateStatusFilter,
  OverviewTableActions.updateEinAusgangFilter,
  OverviewTableActions.updateShowDublettenFilter,
  OverviewTableActions.updateCreatedDateFilter,
  OverviewTableActions.updateBelegDateFilter,
  OverviewTableActions.resetSingleFilter,
  OverviewTableActions.resetAllFilter,
  OverviewTableActions.setBetragvonFilter,
  OverviewTableActions.setBetragbisFilter,
  OverviewTableActions.setBelegtypFilter,
  OverviewTableActions.setZahlungsartFilter,
  OverviewTableActions.updateUmsatzZugeordnetFilter,
];

export const overviewTableReducer = createReducer(
  initialOverviewTableState,

  on(OverviewTableActions.readBelege, state => ({
    ...state,
    isLoading: true,
  })),

  on(OverviewTableActions.setDisplayedIds, (state, {belegDtos}) => ({
    ...state,
    displayedIds: belegDtos.map(beleg => beleg.id),
    isLoading: false,
  })),

  /*
   * Reducer für das Hinzufügen einer ID zur Liste der angezeigten IDs.
   * Dieser Reducer aktualisiert den State, indem er eine neue ID zur Liste hinzufügt.
   * Es wird sichergestellt, dass die ID eindeutig ist und nicht dupliziert wird.
   * Wenn die ID bereits in der Liste vorhanden ist, bleibt die Liste unverändert.
   *
   * INFO: Wenn Filter aktiv sind, wird der Beleg nicht zur Anzeige hinzugefügt,
   * da er möglicherweise nicht den Filterkriterien entspricht. Der nächste
   * readBelege-Aufruf zeigt den Beleg korrekt an, falls er den Filtern entspricht.
   */
  on(OverviewTableActions.addDisplayedId, (state, {belegDto}) => {
    if (hasActiveFilters(state.filterDto)) {
      return state;
    }
    return {
      ...state,
      displayedIds: state.displayedIds.includes(belegDto.id)
        ? state.displayedIds
        : [belegDto.id, ...state.displayedIds],
    };
  }),

  on(OverviewTableActions.clearDisplayedBelege, state => ({
    ...state,
    displayedIds: [],
    isLoading: false,
  })),

  on(OverviewTableActions.showFilter, state => ({
    ...state,
    filterVisible: true,
  })),

  on(OverviewTableActions.hideFilter, state => ({
    ...state,
    filterVisible: false,
  })),

  on(OverviewTableActions.updateSearchFilter, (state, {searchValue}) => {
    let updatedTextFilter: string[] | undefined;

    let updatedFilterChipInputIdentifiers: FilterChipInputIdentifier[] = [...state.chipInputIdentifiers];

    if (searchValue.length === 0) {
      updatedTextFilter = undefined;

      // INFO: Wenn der Textfilter leer ist, dann wird der Filter aus dem ChipInputIdentifier entfernt
      updatedFilterChipInputIdentifiers = deleteFilterChipInputIdentifierByFilterName(
        state.chipInputIdentifiers,
        'textfilter',
      );
    } else {
      updatedTextFilter = searchValue.split(' ');

      // INFO: Wenn der Textfilter nicht leer ist, dann wird der Filter in den ChipInputIdentifier hinzugefügt
      const identifier = createFilterChipInputIdentifier(searchValue, 'textfilter', searchValue);

      updatedFilterChipInputIdentifiers = updateFilterChipInputIdentifier(updatedFilterChipInputIdentifiers, [
        identifier,
      ]);
    }

    return {
      ...state,
      filterDto: {
        ...state.filterDto,
        textfilter: updatedTextFilter,
      },
      chipInputIdentifiers: updatedFilterChipInputIdentifiers,
    };
  }),

  on(OverviewTableActions.updateStatusFilter, (state, {offen, bearbeitet, festgeschrieben, storniert}) => {
    /*
     * INFO:
     * Da die Status als einzelne Filter behandelt werden,
     * im selben Zuge gelöscht als auch hinzugefügt werden können und dies ebenfalls im selben Reducer passiert,
     * wird die aktuelle Liste der FilterChipInputIdentifier in eine neue Liste kopiert
     * und der Store dann mit der neuen Liste aktualisiert.
     */
    let currentFilterChipInputIdentifiers: FilterChipInputIdentifier[] = [...state.chipInputIdentifiers];

    // INFO: FilterChipInputIdentifier für neue Status Filter erstellen
    let newFilterChipInputIdentifiers: FilterChipInputIdentifier[] = [];

    // INFO: FilterChipInputIdentifier für die Status hinzufügen oder entfernen
    if (offen) {
      newFilterChipInputIdentifiers.push(createFilterChipInputIdentifier('Offene Belege', 'offen', offen));
    } else {
      currentFilterChipInputIdentifiers = deleteFilterChipInputIdentifierByFilterName(
        currentFilterChipInputIdentifiers,
        'offen',
      );
    }

    if (bearbeitet) {
      newFilterChipInputIdentifiers.push(
        createFilterChipInputIdentifier('Bearbeitete Belege', 'bearbeitet', bearbeitet),
      );
    } else {
      currentFilterChipInputIdentifiers = deleteFilterChipInputIdentifierByFilterName(
        currentFilterChipInputIdentifiers,
        'bearbeitet',
      );
    }

    if (festgeschrieben) {
      newFilterChipInputIdentifiers.push(
        createFilterChipInputIdentifier('Fertiggestellte Belege', 'festgeschrieben', festgeschrieben),
      );
    } else {
      currentFilterChipInputIdentifiers = deleteFilterChipInputIdentifierByFilterName(
        currentFilterChipInputIdentifiers,
        'festgeschrieben',
      );
    }

    if (storniert) {
      newFilterChipInputIdentifiers.push(createFilterChipInputIdentifier('Stornierte Belege', 'storniert', storniert));
    } else {
      currentFilterChipInputIdentifiers = deleteFilterChipInputIdentifierByFilterName(
        currentFilterChipInputIdentifiers,
        'storniert',
      );
    }

    const updatedFilterChipInputIdentifiers = updateFilterChipInputIdentifier(
      currentFilterChipInputIdentifiers,
      newFilterChipInputIdentifiers,
    );

    return {
      ...state,
      filterDto: {
        ...state.filterDto,
        offen,
        bearbeitet,
        festgeschrieben,
        storniert,
      },
      chipInputIdentifiers: updatedFilterChipInputIdentifiers,
    };
  }),

  on(OverviewTableActions.updateEinAusgangFilter, (state, {einAusgang}) => {
    /*
     * INFO:
     * Da eingangsbeleg und ausgangsbeleg als einzelne Filter behandelt werden,
     * im selben Zuge gelöscht als auch hinzugefügt werden können und dies ebenfalls im selben Reducer passiert,
     * wird die aktuelle Liste der FilterChipInputIdentifier in eine neue Liste kopiert
     * und der Store dann mit der neuen Liste aktualisiert.
     */
    let currentFilterChipInputIdentifiers: FilterChipInputIdentifier[] = [...state.chipInputIdentifiers];

    // INFO: FilterChipInputIdentifier für neue eingangsbeleg und oder ausgangsbeleg Filter erstellen
    let newFilterChipInputIdentifiers: FilterChipInputIdentifier[] = [];

    const isEingangsbeleg = einAusgang.includes(BelegbucheinAusgangDTO.Belegeingang) || false;
    if (isEingangsbeleg) {
      // INFO: FilterChipInputIdentifier für eingangsbeleg erstellen
      newFilterChipInputIdentifiers.push(createFilterChipInputIdentifier('Eingang', 'eingangsbeleg', isEingangsbeleg));
    } else {
      // INFO: FilterChipInputIdentifier für eingangsbeleg entfernen
      currentFilterChipInputIdentifiers = deleteFilterChipInputIdentifierByFilterName(
        currentFilterChipInputIdentifiers,
        'eingangsbeleg',
      );
    }

    const isAusgangsbeleg = einAusgang.includes(BelegbucheinAusgangDTO.Belegausgang) || false;
    if (isAusgangsbeleg) {
      // INFO: FilterChipInputIdentifier für ausgangsbeleg erstellen
      newFilterChipInputIdentifiers.push(createFilterChipInputIdentifier('Ausgang', 'ausgangsbeleg', isAusgangsbeleg));
    } else {
      // INFO: FilterChipInputIdentifier für ausgangsbeleg entfernen
      currentFilterChipInputIdentifiers = deleteFilterChipInputIdentifierByFilterName(
        currentFilterChipInputIdentifiers,
        'ausgangsbeleg',
      );
    }

    const updatedFilterChipInputIdentifiers = updateFilterChipInputIdentifier(
      currentFilterChipInputIdentifiers,
      newFilterChipInputIdentifiers,
    );

    return {
      ...state,
      filterDto: {
        ...state.filterDto,
        eingangsbeleg: isEingangsbeleg,
        ausgangsbeleg: isAusgangsbeleg,
      },
      chipInputIdentifiers: updatedFilterChipInputIdentifiers,
    };
  }),

  on(OverviewTableActions.updateShowDublettenFilter, (state, {showDubletten}) => {
    // INFO: FilterChipInputIdentifier für showDubletten aktualisieren
    let updatedFilterChipInputIdentifiers: FilterChipInputIdentifier[] = [...state.chipInputIdentifiers];

    if (showDubletten) {
      // INFO: FilterChipInputIdentifier für showDubletten erstellen
      const showDublettenIdentifier = createFilterChipInputIdentifier('Nur Dubletten', 'dublettenOnly', showDubletten);

      updatedFilterChipInputIdentifiers = updateFilterChipInputIdentifier(updatedFilterChipInputIdentifiers, [
        showDublettenIdentifier,
      ]);
    } else {
      // INFO: FilterChipInputIdentifier für showDubletten entfernen
      updatedFilterChipInputIdentifiers = deleteFilterChipInputIdentifierByFilterName(
        updatedFilterChipInputIdentifiers,
        'dublettenOnly',
      );
    }

    return {
      ...state,
      filterDto: {
        ...state.filterDto,
        dublettenOnly: showDubletten,
      },
      chipInputIdentifiers: updatedFilterChipInputIdentifiers,
    };
  }),

  on(OverviewTableActions.updateCreatedDateFilter, (state, {dateFilter}) => {
    let updatedFilterChipInputIdentifiers: FilterChipInputIdentifier[] = [...state.chipInputIdentifiers];

    // INFO: FilterChipInputIdentifier für HochgeladenVon hinzufügen oder entfernen
    if (dateFilter.from) {
      const hochgeladenVonIdentifier = createFilterChipInputIdentifier(
        'Hochgeladen von ' + moment(dateFilter.from, 'YYYY-MM-DD').format('DD.MM.YYYY'),
        'erstelltVon',
        dateFilter.from,
      );
      updatedFilterChipInputIdentifiers = updateFilterChipInputIdentifier(updatedFilterChipInputIdentifiers, [
        hochgeladenVonIdentifier,
      ]);
    } else {
      updatedFilterChipInputIdentifiers = deleteFilterChipInputIdentifierByFilterName(
        updatedFilterChipInputIdentifiers,
        'erstelltVon',
      );
    }

    // INFO: FilterChipInputIdentifier für HochgeladenBis hinzufügen oder entfernen
    if (dateFilter.to) {
      const hochgeladenBisIdentifier = createFilterChipInputIdentifier(
        'Hochgeladen bis ' + moment(dateFilter.to, 'YYYY-MM-DD').format('DD.MM.YYYY'),
        'erstelltBis',
        dateFilter.to,
      );
      updatedFilterChipInputIdentifiers = updateFilterChipInputIdentifier(updatedFilterChipInputIdentifiers, [
        hochgeladenBisIdentifier,
      ]);
    } else {
      updatedFilterChipInputIdentifiers = deleteFilterChipInputIdentifierByFilterName(
        updatedFilterChipInputIdentifiers,
        'erstelltBis',
      );
    }

    return {
      ...state,
      filterDto: {
        ...state.filterDto,
        erstelltVon: dateFilter.from || undefined,
        erstelltBis: dateFilter.to || undefined,
      },
      chipInputIdentifiers: updatedFilterChipInputIdentifiers,
    };
  }),

  on(OverviewTableActions.updateBelegDateFilter, (state, {dateFilter}) => {
    let updatedFilterChipInputIdentifiers: FilterChipInputIdentifier[] = [...state.chipInputIdentifiers];

    // INFO: FilterChipInputIdentifier für BelegdatumVon hinzufügen oder entfernen
    if (dateFilter.from) {
      const belegdatumVonIdentifier = createFilterChipInputIdentifier(
        'Belegdatum von ' + moment(dateFilter.from, 'YYYY-MM-DD').format('DD.MM.YYYY'),
        'datumVon',
        dateFilter.from,
      );
      updatedFilterChipInputIdentifiers = updateFilterChipInputIdentifier(updatedFilterChipInputIdentifiers, [
        belegdatumVonIdentifier,
      ]);
    } else {
      updatedFilterChipInputIdentifiers = deleteFilterChipInputIdentifierByFilterName(
        updatedFilterChipInputIdentifiers,
        'datumVon',
      );
    }

    // INFO: FilterChipInputIdentifier für BelegdatumBis hinzufügen oder entfernen
    if (dateFilter.to) {
      const belegdatumBisIdentifier = createFilterChipInputIdentifier(
        'Belegdatum bis ' + moment(dateFilter.to, 'YYYY-MM-DD').format('DD.MM.YYYY'),
        'datumBis',
        dateFilter.to,
      );
      updatedFilterChipInputIdentifiers = updateFilterChipInputIdentifier(updatedFilterChipInputIdentifiers, [
        belegdatumBisIdentifier,
      ]);
    } else {
      updatedFilterChipInputIdentifiers = deleteFilterChipInputIdentifierByFilterName(
        updatedFilterChipInputIdentifiers,
        'datumBis',
      );
    }

    return {
      ...state,
      filterDto: {
        ...state.filterDto,
        datumVon: dateFilter.from || undefined,
        datumBis: dateFilter.to || undefined,
      },
      chipInputIdentifiers: updatedFilterChipInputIdentifiers,
    };
  }),

  on(OverviewTableActions.resetSingleFilter, (state, {id}) => {
    // INFO: FilterBelegDTO aktualisieren
    const identifier = state.chipInputIdentifiers.find(chip => {
      return chip.filterId === id;
    })!;

    let newFilterDto: FilterBelegDTO = {
      ...state.filterDto,
    };

    /*
     * INFO:
     * Wenn der zu entfernende Filter eine einzelne Propertie im FilterBelegDTO ist,
     * dann wird diese auf undefined gesetzt.
     *
     * Vorgangsart bildet hierbei einen Sonderfall, da es sich um ein Array handelt.
     * In diesem Fall wird der zu entfernende Wert anhand der Id aus dem Array gefiltert
     * und das neue Array in das DTO übernommen.
     */
    if (identifier.filterName === 'vorgangsart') {
      let newVorgangsartIds = state.filterDto.vorgangsartIds!.filter(id => {
        return id !== identifier.filterValue;
      });

      newFilterDto.vorgangsartIds = newVorgangsartIds;
    } else if (identifier.filterName === 'zahlungsarten') {
      let newZahlungsarten = state.filterDto.zahlungsarten!.filter(id => {
        return id !== identifier.filterValue;
      });

      newFilterDto.zahlungsarten = newZahlungsarten;
    } else {
      newFilterDto = {
        ...state.filterDto,
        [identifier.filterName]: undefined,
      };
    }

    // INFO: chipInputIdentifier aktualisieren
    const updatedFilterChipInputIdentifiers = state.chipInputIdentifiers.filter(chip => {
      return chip.filterId !== id;
    });

    return {
      ...state,
      filterDto: newFilterDto,
      chipInputIdentifiers: updatedFilterChipInputIdentifiers,
    };
  }),

  on(OverviewTableActions.resetAllFilter, state => ({
    ...state,
    filterDto: initialOverviewTableState.filterDto,
    chipInputIdentifiers: initialOverviewTableState.chipInputIdentifiers,
  })),

  on(OverviewTableActions.updatePageIndex, (state, {pageIndex}) => ({
    ...state,
    pageableDto: {
      ...state.pageableDto,
      offset: pageIndex * state.pageableDto.limit!,
    },
  })),

  on(BelegEntitiesActions.startReadingBelege, state => ({
    ...state,
    selectedIds: [],
  })),

  on(OverviewTableActions.toggleBeleg, (state, {belegId}) => {
    let selectedIds = [...state.selectedIds];

    const checked = state.selectedIds.includes(belegId);
    if (checked) {
      selectedIds = selectedIds.filter(id => id !== belegId);
    } else {
      selectedIds.push(belegId);
    }

    return {
      ...state,
      selectedIds,
    };
  }),

  on(OverviewTableActions.toggleBelege, (state, {belegIds}) => {
    let selectedIds: string[] = [...state.selectedIds];

    belegIds.forEach(belegId => {
      const checked = state.selectedIds.includes(belegId);
      if (checked) {
        selectedIds = selectedIds.filter(id => id !== belegId);
      } else {
        selectedIds.push(belegId);
      }
    });

    return {
      ...state,
      selectedIds,
    };
  }),

  on(OverviewTableActions.selectAll, state => ({
    ...state,
    selectedIds: [...state.displayedIds],
  })),

  on(OverviewTableActions.unselectAll, state => ({
    ...state,
    selectedIds: [],
  })),

  on(OverviewTableActions.toggleAll, (state, {belegIds}) => {
    let selectedIds: string[] = [...state.selectedIds];

    // INFO: Wenn bereits entweder alle oder einige Elemente ausgewählt sind, dann alle deselektieren
    if (selectedIds.length === belegIds.length || selectedIds.length > 0) {
      selectedIds = [];
    }

    // INFO: Wenn noch keine Elemente ausgewählt sind, dann alle selektieren
    else {
      selectedIds = belegIds;
    }

    return {
      ...state,
      selectedIds,
    };
  }),

  on(OverviewTableActions.updateSortingEnum, (state, {sorting}) => ({
    ...state,
    pageableDto: {
      ...state.pageableDto,
      sorting,
    },
  })),

  on(OverviewTableActions.changeTableSettings, (state, action) => ({
    ...state,
    tableSettings: action.tableSettings,
  })),

  on(OverviewTableActions.changeTableSettingsAreInitialized, (state, action) => ({
    ...state,
    tableSettingsAreInitialized: action.tableSettingsAreInitialized,
  })),

  on(OverviewTableActions.updateBelegSuccess, state => ({
    ...state,
    selectedIds: [],
  })),

  on(BelegEntitiesActions.stipulateBelegSuccess, state => ({
    ...state,
    selectedIds: [],
  })),

  on(BelegEntitiesActions.moveSuccess, (state, {belegId}) => ({
    ...state,
    selectedIds: state.selectedIds.filter(id => id !== belegId),
    displayedIds: state.displayedIds.filter(id => id !== belegId),
  })),

  on(BelegEntitiesActions.deleteBelegSuccess, (state, {belegId}) => ({
    ...state,
    selectedIds: state.selectedIds.filter(id => id !== belegId),
    displayedIds: state.displayedIds.filter(id => id !== belegId),
  })),

  on(OverviewTableActions.setBetragvonFilter, (state, {betragVon}) => {
    let updatedFilterChipInputIdentifiers: FilterChipInputIdentifier[] = [];
    if (betragVon != null) {
      const identifier = createFilterChipInputIdentifier(
        'Betrag von ' + formatBetrag(betragVon) + ' €',
        'betragVon',
        betragVon,
      );

      updatedFilterChipInputIdentifiers = updateFilterChipInputIdentifier(
        [...state.chipInputIdentifiers],
        [identifier],
      );
    } else {
      // INFO: Wenn der BetragVon-Filter null ist, dann wird der Filter aus den ChipInputIdentifiers entfernt
      updatedFilterChipInputIdentifiers = deleteFilterChipInputIdentifierByFilterName(
        state.chipInputIdentifiers,
        'betragVon',
      );
    }

    return {
      ...state,
      filterDto: {
        ...state.filterDto,
        betragVon,
      },
      chipInputIdentifiers: updatedFilterChipInputIdentifiers,
    };
  }),

  on(OverviewTableActions.setBetragbisFilter, (state, {betragBis}) => {
    let updatedFilterChipInputIdentifiers: FilterChipInputIdentifier[] = [];

    if (betragBis != null) {
      const identifier = createFilterChipInputIdentifier(
        'Betrag bis ' + formatBetrag(betragBis) + ' €',
        'betragBis',
        betragBis,
      );

      updatedFilterChipInputIdentifiers = updateFilterChipInputIdentifier(
        [...state.chipInputIdentifiers],
        [identifier],
      );
    } else {
      // INFO: Wenn der BetragBis-Filter null ist, dann wird der Filter aus den ChipInputIdentifiers entfernt
      updatedFilterChipInputIdentifiers = deleteFilterChipInputIdentifierByFilterName(
        state.chipInputIdentifiers,
        'betragBis',
      );
    }

    return {
      ...state,
      filterDto: {
        ...state.filterDto,
        betragBis,
      },
      chipInputIdentifiers: updatedFilterChipInputIdentifiers,
    };
  }),

  on(OverviewTableActions.setBelegtypFilter, (state, {selectedVorgangsartIds, allVorgangsartDtos}) => {
    // INFO: FilterChipInputIdentifiers aktualisieren
    let updatedFilterChipInputIdentifiers: FilterChipInputIdentifier[] = [...state.chipInputIdentifiers];

    /*
     * INFO:
     * Vorgangsarten, die der Anwender abgewählt hat, müssen aus den ChipInputIdentifiers entfernt werden.
     * Hierbei können diese über den ‘filterName‘ (vorgangsart) und den 'filterValue' identifiziert werden,
     * da der 'filterValue' einer Vorgangsart immer eine eindeutige UUID ist, welche serverseitig generiert wird.
     */
    const currentVorgangsartValues = updatedFilterChipInputIdentifiers
      .filter(chip => {
        return chip.filterName === 'vorgangsart';
      })
      .map(chip => {
        return chip.filterValue;
      });

    for (let currentVorgangsartId of currentVorgangsartValues) {
      // INFO: In diesem Fall ist die currentVorgangsartId immer ein String und darf / muss daher gecastet werden
      if (!selectedVorgangsartIds.includes(currentVorgangsartId as string)) {
        const indexToRemove = updatedFilterChipInputIdentifiers.findIndex(chip => {
          return chip.filterName == 'vorgangsart' && chip.filterValue === currentVorgangsartId;
        });

        updatedFilterChipInputIdentifiers.splice(indexToRemove, 1);
      }
    }

    /*
     * INFO: Vorgangsarten, die der Anwender neu ausgewählt hat,
     * müssen zu den ChipInputIdentifiers hinzugefügt werden.
     */
    for (let vorgangsartId of selectedVorgangsartIds) {
      if (!currentVorgangsartValues.includes(vorgangsartId)) {
        const vorgangsartDto = allVorgangsartDtos!.find(vorgangsartDto => {
          return vorgangsartId === vorgangsartDto.id;
        });

        if (!!vorgangsartDto) {
          updatedFilterChipInputIdentifiers.push(
            createFilterChipInputIdentifier('Belegtyp ' + vorgangsartDto.bezeichnung, 'vorgangsart', vorgangsartId),
          );
        }
      }
    }

    return {
      ...state,
      filterDto: {
        ...state.filterDto,
        vorgangsartIds: selectedVorgangsartIds,
      },
      chipInputIdentifiers: updatedFilterChipInputIdentifiers,
    };
  }),

  on(OverviewTableActions.toggleBelege, state => {
    return {
      ...state,
    };
  }),

  on(OverviewTableActions.setZahlungsartFilter, (state, {zahlungsarten}) => {
    // INFO: FilterChipInputIdentifiers aktualisieren
    let updatedFilterChipInputIdentifiers: FilterChipInputIdentifier[] = [...state.chipInputIdentifiers];

    /*
     * INFO:
     * Zahlungsarten, die der Anwender abgewählt hat, müssen aus den ChipInputIdentifiers entfernt werden.
     * Hierbei können diese über den 'filterName' (zahlungsart) und den 'filterValue' identifiziert werden,
     * da der 'filterValue' einer Zahlungsart immer ein eindeutiger Enum-Wert ist.
     */
    const currentZahlungsartValues = updatedFilterChipInputIdentifiers
      .filter(chip => {
        return chip.filterName === 'zahlungsarten';
      })
      .map(chip => {
        return chip.filterValue;
      });

    for (let currentZahlungsart of currentZahlungsartValues) {
      // INFO: In diesem Fall ist die currentZahlungsart immer ein String und darf / muss daher gecastet werden
      if (!zahlungsarten.includes(currentZahlungsart as BelegZahlungsartDTO)) {
        const indexToRemove = updatedFilterChipInputIdentifiers.findIndex(chip => {
          return chip.filterName == 'zahlungsarten' && chip.filterValue === currentZahlungsart;
        });

        updatedFilterChipInputIdentifiers.splice(indexToRemove, 1);
      }
    }

    /*
     * INFO: Zahlungsarten, die der Anwender neu ausgewählt hat,
     * müssen zu den ChipInputIdentifiers hinzugefügt werden.
     */
    for (let zahlungsart of zahlungsarten) {
      if (!currentZahlungsartValues.includes(zahlungsart)) {
        let label = '';

        switch (zahlungsart) {
          case BelegZahlungsartDTO.Ueberweisung:
            label = 'Überweisung';
            break;
          case BelegZahlungsartDTO.AmazonPayments:
            label = 'Amazon Payments';
            break;
          default:
            label = zahlungsart;
        }

        updatedFilterChipInputIdentifiers.push(
          createFilterChipInputIdentifier('Zahlungsart ' + label, 'zahlungsarten', zahlungsart),
        );
      }
    }

    // INFO: Konvertiere BelegZahlungsartDTO[] zu string[] für das FilterBelegDTO
    const zahlungsartenStrings = zahlungsarten.map(z => z as string);

    return {
      ...state,
      filterDto: {
        ...state.filterDto,
        zahlungsarten: zahlungsartenStrings,
      },
      chipInputIdentifiers: updatedFilterChipInputIdentifiers,
    };
  }),

  on(OverviewTableActions.updateUmsatzZugeordnetFilter, (state, {umsatzZugeordnetFilter}) => {
    /*
     * INFO:
     * Da umsatzZugeordnet und keinUmsatzZugeordnet als einzelne Filter behandelt werden,
     * im selben Zuge gelöscht als auch hinzugefügt werden können und dies ebenfalls im selben Reducer passiert,
     * wird die aktuelle Liste der FilterChipInputIdentifier in eine neue Liste kopiert
     * und der Store dann mit der neuen Liste aktualisiert.
     */
    let currentFilterChipInputIdentifiers: FilterChipInputIdentifier[] = [...state.chipInputIdentifiers];

    // INFO: FilterChipInputIdentifier für neue Umsatzzuordnung Filter erstellen
    let newFilterChipInputIdentifiers: FilterChipInputIdentifier[] = [];

    const hasUmsatzZugeordnet = umsatzZugeordnetFilter.some(v => v.umsatzZugeordnet === true);
    const hasKeinUmsatzZugeordnet = umsatzZugeordnetFilter.some(v => v.keinUmsatzZugeordnet === true);

    // INFO: FilterChipInputIdentifier für die Umsatzzuordnung hinzufügen oder entfernen
    if (hasUmsatzZugeordnet) {
      newFilterChipInputIdentifiers.push(
        createFilterChipInputIdentifier('Umsatz zugeordnet', 'umsatzZugeordnet', true),
      );
    } else {
      currentFilterChipInputIdentifiers = deleteFilterChipInputIdentifierByFilterName(
        currentFilterChipInputIdentifiers,
        'umsatzZugeordnet',
      );
    }

    if (hasKeinUmsatzZugeordnet) {
      newFilterChipInputIdentifiers.push(
        createFilterChipInputIdentifier('Kein Umsatz zugeordnet', 'keinUmsatzZugeordnet', true),
      );
    } else {
      currentFilterChipInputIdentifiers = deleteFilterChipInputIdentifierByFilterName(
        currentFilterChipInputIdentifiers,
        'keinUmsatzZugeordnet',
      );
    }

    const updatedFilterChipInputIdentifiers = updateFilterChipInputIdentifier(
      currentFilterChipInputIdentifiers,
      newFilterChipInputIdentifiers,
    );

    return {
      ...state,
      filterDto: {
        ...state.filterDto,
        umsatzZugeordnet: hasUmsatzZugeordnet || undefined,
        keinUmsatzZugeordnet: hasKeinUmsatzZugeordnet || undefined,
      },
      chipInputIdentifiers: updatedFilterChipInputIdentifiers,
    };
  }),

  /*
   * INFO:
   * Jede Filteränderung springt zurück auf Seite 1 (offset = 0). Das combineLatest in der
   * OverviewComponent feuert bei Filteränderung sofort ein readBelege — mit dem alten Offset
   * ergäbe das eine leere Liste, sobald die gefilterte Menge weniger Treffer als der Offset hat.
   *
   * Der Reset greift bewusst auch bei fachlichen No-op-Filteränderungen (z.B. Setzen desselben
   * Werts): Jede Filterinteraktion gilt als neue Suche und beginnt daher auf der ersten Seite.
   *
   * `updateSortingEnum` steht bewusst NICHT in der Liste: Ein Sortierwechsel ändert die Größe der
   * Ergebnismenge nicht, die aktuelle Seite bleibt also gültig — der Anwender behält seine Position.
   *
   * NgRx komponiert mehrere on-Registrierungen derselben Action in Registrierungsreihenfolge;
   * dieser Handler läuft nach den eigentlichen Filter-Handlern und lässt deren Ergebnis unberührt.
   */
  on(...filterChangingActions, (state): OverviewTableState => {
    // INFO: Referenz nur ändern, wenn nötig — sonst emittiert der pageableDto-Selektor bei jeder
    // Filterinteraktion und die Übersicht lädt doppelt.
    if (state.pageableDto.offset === 0) return state;

    return {
      ...state,
      pageableDto: {
        ...state.pageableDto,
        offset: 0,
      },
    };
  }),
);

/**
 * Erstellt einen FilterChipInputIdentifier.
 *
 * @param filterLabel Label, dass der Anwender sehen soll
 * @param filterName Name des Filters im FilterBelegDTO
 * @param filterValue Der Wert des Filters
 */
function createFilterChipInputIdentifier(
  filterLabel: string,
  filterName: string,
  filterValue: any,
): FilterChipInputIdentifier {
  return {
    filterId: uuid(),
    filterLabel,
    filterName,
    filterValue,
  };
}

/**
 * Aktualisierte die Liste der FilterChipInputIdentifier und gibt diese zurück.
 *
 * @param currentFilterChipInputIdentifiers Die aktuelle Liste der FilterChipInputIdentifier
 * @param filterChipInputIdentifiersToAdd Die FilterChipInputIdentifier, die hinzugefügt werden sollen
 */
function updateFilterChipInputIdentifier(
  currentFilterChipInputIdentifiers: FilterChipInputIdentifier[],
  filterChipInputIdentifiersToAdd: FilterChipInputIdentifier[],
): FilterChipInputIdentifier[] {
  for (let identifierToAdd of filterChipInputIdentifiersToAdd) {
    const indexToRemove = currentFilterChipInputIdentifiers.findIndex(chip => {
      return chip.filterName === identifierToAdd.filterName;
    });

    // INFO: Wenn der identifier bereits in der Liste vorhanden ist, dann wird dieser auf derselben Position ersetzt
    if (indexToRemove !== -1) {
      currentFilterChipInputIdentifiers.splice(indexToRemove, 1, identifierToAdd);
    } else {
      currentFilterChipInputIdentifiers = [...currentFilterChipInputIdentifiers, identifierToAdd];
    }
  }

  return currentFilterChipInputIdentifiers;
}

/**
 * Löscht einen FilterChipInputIdentifier anhand des Filternamens.
 * Gibt anschließend die aktualisierte Liste der FilterChipInputIdentifier zurück.
 *
 * @param chipInputIdentifiers Die aktuelle Liste der FilterChipInputIdentifier
 * @param filterName Der Name des Filters im FilterBelegDTO, der entfernt werden soll
 */
function deleteFilterChipInputIdentifierByFilterName(
  chipInputIdentifiers: FilterChipInputIdentifier[],
  filterName: string,
): FilterChipInputIdentifier[] {
  return chipInputIdentifiers.filter(chip => {
    return chip.filterName !== filterName;
  });
}
